/**
 * Layout of the public career site, saved on the firm (`jobBoardLayout`) by the job board customization
 * page: the header banner, how the offers are listed (presentation, badges, visitor filters), what the
 * offer page shows and where the employer branding sections sit. A missing or invalid value takes the default, so `null` is the default layout.
 */

export const CAREER_HERO_STYLES = ['card', 'fullWidth'] as const
export type TCareerHeroStyle = (typeof CAREER_HERO_STYLES)[number]

export const CAREER_HERO_ALIGNS = ['left', 'center'] as const
export type TCareerHeroAlign = (typeof CAREER_HERO_ALIGNS)[number]

export const CAREER_HERO_HEIGHTS = ['compact', 'standard', 'tall'] as const
export type TCareerHeroHeight = (typeof CAREER_HERO_HEIGHTS)[number]

/** Veil over the banner background image, so that its text stays readable */
export const CAREER_HERO_OVERLAYS = ['none', 'light', 'medium', 'strong'] as const
export type TCareerHeroOverlay = (typeof CAREER_HERO_OVERLAYS)[number]

export const CAREER_LOGO_POSITIONS = ['side', 'above', 'hidden'] as const
export type TCareerLogoPosition = (typeof CAREER_LOGO_POSITIONS)[number]

/** Offer list: cards in a grid, compact rows, or cards with the offer's cover image */
export const CAREER_OFFER_LAYOUTS = ['grid', 'list', 'visual'] as const
export type TCareerOfferLayout = (typeof CAREER_OFFER_LAYOUTS)[number]

/** Details shown on each offer of the list (from the offer's job board fields) */
export const CAREER_OFFER_BADGES = ['contract', 'workingTime', 'remote', 'location', 'experience', 'salary', 'new'] as const
export type TCareerOfferBadge = (typeof CAREER_OFFER_BADGES)[number]

/** Filters offered to the visitors above the offer list */
export const CAREER_OFFER_FILTERS = ['search', 'contract', 'remote', 'office'] as const
export type TCareerOfferFilter = (typeof CAREER_OFFER_FILTERS)[number]

/** Where the employer branding sections sit on the offer list page */
export const CAREER_SECTIONS_POSITIONS = ['beforeOffers', 'afterOffers'] as const
export type TCareerSectionsPosition = (typeof CAREER_SECTIONS_POSITIONS)[number]

/** An offer published for less than this many days shows the "New" badge */
export const CAREER_NEW_OFFER_DAYS = 14

export type TCareerLayout = {
  hero: {
    style: TCareerHeroStyle
    align: TCareerHeroAlign
    height: TCareerHeroHeight
    overlay: TCareerHeroOverlay
    logoPosition: TCareerLogoPosition
  }
  offers: {
    layout: TCareerOfferLayout
    badges: TCareerOfferBadge[]
    filters: TCareerOfferFilter[]
  }
  offerPage: {
    /** Summary of the job board fields (contract, place, remote, salary, experience) beside the offer */
    keyFacts: boolean
    /** Cover image of the offer above the offer */
    cover: boolean
    /** A few other offers of the same offices below the offer */
    similar: boolean
  }
  sections: {
    position: TCareerSectionsPosition
  }
}

export const defaultCareerLayout = (): TCareerLayout => ({
  hero: { style: 'card', align: 'left', height: 'standard', overlay: 'none', logoPosition: 'side' },
  offers: {
    layout: 'grid',
    badges: ['contract', 'remote', 'location', 'new'],
    filters: ['search', 'contract', 'remote', 'office'],
  },
  offerPage: { keyFacts: true, cover: true, similar: true },
  sections: { position: 'afterOffers' },
})

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const pick = <T extends string>(allowed: readonly T[], value: unknown, fallback: T): T =>
  allowed.includes(value as T) ? value as T : fallback

/** The listed values, in the order of the list, or the default when the value is not an array */
const pickList = <T extends string>(allowed: readonly T[], value: unknown, fallback: T[]): T[] =>
  Array.isArray(value) ? allowed.filter(item => value.includes(item)) : fallback

const pickBoolean = (value: unknown, fallback: boolean) => (typeof value === 'boolean' ? value : fallback)

/** Keeps only the known keys with a valid value: anything else takes the default */
export const sanitizeCareerLayout = (value: unknown): TCareerLayout => {
  const input = isRecord(value) ? value : {}
  const hero = isRecord(input.hero) ? input.hero : {}
  const offers = isRecord(input.offers) ? input.offers : {}
  const offerPage = isRecord(input.offerPage) ? input.offerPage : {}
  const sections = isRecord(input.sections) ? input.sections : {}
  const defaults = defaultCareerLayout()
  return {
    hero: {
      style: pick(CAREER_HERO_STYLES, hero.style, defaults.hero.style),
      align: pick(CAREER_HERO_ALIGNS, hero.align, defaults.hero.align),
      height: pick(CAREER_HERO_HEIGHTS, hero.height, defaults.hero.height),
      overlay: pick(CAREER_HERO_OVERLAYS, hero.overlay, defaults.hero.overlay),
      logoPosition: pick(CAREER_LOGO_POSITIONS, hero.logoPosition, defaults.hero.logoPosition),
    },
    offers: {
      layout: pick(CAREER_OFFER_LAYOUTS, offers.layout, defaults.offers.layout),
      badges: pickList(CAREER_OFFER_BADGES, offers.badges, defaults.offers.badges),
      filters: pickList(CAREER_OFFER_FILTERS, offers.filters, defaults.offers.filters),
    },
    offerPage: {
      keyFacts: pickBoolean(offerPage.keyFacts, defaults.offerPage.keyFacts),
      cover: pickBoolean(offerPage.cover, defaults.offerPage.cover),
      similar: pickBoolean(offerPage.similar, defaults.offerPage.similar),
    },
    sections: {
      position: pick(CAREER_SECTIONS_POSITIONS, sections.position, defaults.sections.position),
    },
  }
}

/** Whether an offer published on `created` is still new on `now` */
export const isNewCareerOffer = (created: unknown, now = new Date()) => {
  if (typeof created !== 'string' && !(created instanceof Date))
    return false
  const time = new Date(created).getTime()
  return !Number.isNaN(time) && now.getTime() - time < CAREER_NEW_OFFER_DAYS * 24 * 60 * 60 * 1000
}
